<template>
  <div ref="root" class="mc-picker">
    <div
      class="form-control mc-toggle d-flex justify-content-between align-items-center"
      :class="{ 'mc-disabled': disabled }"
      role="button"
      :tabindex="disabled ? -1 : 0"
      @click="toggleOpen"
      @keydown.enter.prevent="toggleOpen"
    >
      <span class="text-truncate" :class="{ 'text-muted': !summary }">
        {{ summary || placeholder }}
      </span>
      <b-icon icon="chevron-down" class="ml-2 flex-shrink-0" />
    </div>

    <div v-if="open" class="mc-dropdown border rounded shadow-sm bg-white">
      <div class="p-2 border-bottom">
        <b-form-input
          v-model="search"
          type="search"
          placeholder="Search conditions"
          autofocus
        />
      </div>
      <div class="mc-list p-2">
        <!-- "Other" pinned at the top, like the mobile app -->
        <b-form-checkbox :checked="otherChecked" @change="onOtherToggled">
          Other
        </b-form-checkbox>
        <b-form-input
          v-if="otherChecked"
          :value="other"
          type="text"
          class="my-2"
          placeholder="Enter other condition"
          @input="(value) => $emit('update:other', value)"
        />

        <b-form-checkbox
          v-for="option in filteredOptions"
          :key="option"
          :checked="selected.includes(option)"
          @change="(checked) => onOptionToggled(option, checked)"
        >
          {{ option }}
        </b-form-checkbox>
        <div v-if="!filteredOptions.length" class="text-muted small px-1 py-2">
          No conditions match "{{ search }}"
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MEDICAL_CONDITION_OPTIONS } from '@/utils/tradieProfileFields'

/**
 * Searchable multi-select for medical conditions, mirroring the mobile app's
 * picker: alphabetical list from the shared options, search box, and an
 * "Other" entry pinned at the top that reveals a free-text input.
 *
 * Usage: <MedicalConditionsPicker :selected.sync="..." :other.sync="..." />
 * Serialise for the API with serializeMedicalConditions(selected, other).
 */
export default {
  name: 'MedicalConditionsPicker',

  props: {
    selected: {
      type: Array,
      default: () => [],
    },
    other: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select Medical Conditions',
    },
  },

  data() {
    return {
      open: false,
      search: '',
      otherChecked: !!(this.other && this.other.trim()),
    }
  },

  computed: {
    filteredOptions() {
      const query = this.search.trim().toLowerCase()
      if (!query) return MEDICAL_CONDITION_OPTIONS
      return MEDICAL_CONDITION_OPTIONS.filter((option) =>
        option.toLowerCase().includes(query),
      )
    },

    summary() {
      const parts = this.selected.slice()
      const extra = this.other && this.other.trim()
      if (extra) parts.push(extra)
      return parts.join(', ')
    },
  },

  watch: {
    // Prefill can land after mount (async /auth/me) — reflect it.
    other(value) {
      if (value && value.trim()) this.otherChecked = true
    },
  },

  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown)
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown)
  },

  methods: {
    toggleOpen() {
      if (this.disabled) return
      this.open = !this.open
      if (!this.open) this.search = ''
    },

    onDocumentMousedown(event) {
      if (this.open && !this.$refs.root.contains(event.target)) {
        this.open = false
        this.search = ''
      }
    },

    onOptionToggled(option, checked) {
      const next = this.selected.filter((item) => item !== option)
      if (checked) next.push(option)
      this.$emit('update:selected', next)
    },

    onOtherToggled(checked) {
      this.otherChecked = checked
      if (!checked) this.$emit('update:other', '')
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-picker {
  position: relative;
}

.mc-toggle {
  cursor: pointer;
  height: auto;
  min-height: calc(1.5em + 0.75rem + 2px);
}

.mc-disabled {
  cursor: default;
  background-color: #e9ecef;
  opacity: 1;
  pointer-events: none;
}

.mc-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 2px;
}

.mc-list {
  max-height: 240px;
  overflow-y: auto;
}
</style>
